import React from 'react';
import { Sparkles, Wand2, BarChart3, Plus, Bot, FolderPlus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useSonas } from '../SonasContext';
import { useAuth } from '@/integrations/supabase/AuthProvider';
import { cn } from "@/lib/utils";
import CreateFolderDialog from './CreateFolderDialog';

const ProjectGrid = () => {
  const { 
    setIsCreateDialogOpen, 
    isCreateFolderOpen, 
    setIsCreateFolderOpen, 
    createFolder 
  } = useSonas();
  const { profile } = useAuth();

  const handleStart = () => setIsCreateDialogOpen(true);

  const cards = [
    { id: 'clean', title: 'Clean Start', description: 'Start a fresh system with your AI co-pilot.', icon: Sparkles, color: 'text-sonas-accent' },
    { id: 'remix', title: 'Remix Components', description: 'Bring in battle-tested game modules.', icon: Wand2, color: 'text-blue-500' },
    { id: 'trending', title: 'Viral Patterns', description: 'Adapt popular game loops to your project.', icon: BarChart3, color: 'text-purple-500' }
  ];

  const firstName = profile?.full_name?.split(' ')[0] ?? "Architect";

  return (
    <div className="p-8 md:p-12 space-y-12 max-w-7xl mx-auto overflow-y-auto h-full sonas-scrollbar relative">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <div className="absolute top-[-5%] left-[-5%] w-[30%] h-[30%] bg-sonas-accent/5 rounded-full blur-[100px]" />
      </div>

      <section className="space-y-6 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <h1 className="text-4xl md:text-6xl font-syne font-bold tracking-tight">
              Good day, {firstName}.
            </h1>
            <p className="text-sonas-muted text-base md:text-lg max-w-xl font-medium opacity-80">
              The forge is hot. What are we building today?
            </p>
          </div>
          
          <div className="flex gap-3">
             <Button 
               variant="outline" 
               className="h-11 rounded-xl border-white/5 bg-white/5 hover:bg-white/10 text-xs font-bold gap-2 px-5"
               onClick={() => setIsCreateFolderOpen(true)}
             >
               <FolderPlus size={16} /> New Folder
             </Button>
             <Button 
               className="h-11 rounded-xl bg-white text-black hover:bg-white/90 font-bold gap-2 text-xs px-6 shadow-xl transition-all active:scale-95"
               onClick={handleStart}
             >
               <Plus className="h-4 w-4" /> Start Project
             </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="px-3 py-1.5 bg-sonas-accent/10 border border-sonas-accent/20 rounded-full flex items-center gap-2">
            <Bot className="h-3.5 w-3.5 text-sonas-accent" />
            <span className="text-[10px] font-bold text-sonas-accent uppercase tracking-[0.1em]">Architectural Assistant Online</span>
          </div>
        </div>
      </section>

      <div className="space-y-8 relative z-10">
        <div className="flex items-center justify-between border-b border-white/5 pb-4">
          <h2 className="text-[10px] font-bold opacity-40 uppercase tracking-[0.2em]">Templates</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-[1000px]">
          {cards.map((card) => (
            <button
              key={card.id}
              onClick={handleStart}
              className="bg-[#0e0e0e] border border-white/[0.05] relative aspect-square rounded-[32px] p-8 flex flex-col justify-between text-left transition-all group hover:bg-[#121212] hover:border-sonas-accent/20 hover:-translate-y-1 active:scale-[0.98] shadow-2xl"
            >
              <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center transition-all bg-white/[0.03] border border-white/5 group-hover:bg-sonas-accent group-hover:border-sonas-accent")}>
                <card.icon className={cn("h-6 w-6 transition-colors", card.color, "group-hover:text-sonas-bg")} />
              </div>

              <div className="space-y-2">
                <h3 className="text-xl md:text-2xl font-bold font-syne">{card.title}</h3>
                <p className="text-xs md:text-sm text-sonas-muted leading-relaxed opacity-70 group-hover:opacity-100 transition-opacity">{card.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <CreateFolderDialog 
        open={isCreateFolderOpen} 
        onOpenChange={setIsCreateFolderOpen} 
        onCreate={createFolder} 
      />
    </div>
  );
};

export default ProjectGrid;